/**
 * useReferenceMarkerActions.js
 *
 * Extracts all write-side callbacks from ReferenceMarker into a single
 * custom hook, following the Single Responsibility Principle.
 *
 * Read concerns (Y.Map observation) live in useReferenceMarkData.
 * Write concerns (mutations, navigation) live here.
 */

import {useCallback, useMemo} from 'react';
import * as Y from 'yjs';
import useYDocStore from '../../../../../stores/useYDocStore';
import useReferenceNumberStore from '../../../stores/useReferenceNumberStore';
import useSourcePackageBuilderStore from '../../../../../stores/useSourcePackageBuilderStore';
import {addReference, generateLocalId, removeReferenceMark} from '../../../yMaps/referencesYMap';
import {removeSuggestedReference} from '../../../yMaps/claimsYMap';

/**
 * Build the `addReference` payload for a suggested/alternative citation object shaped like a
 * `SegmentResponse` (docId, content, docTitle, index/offset or start/end, docClassificationLevel, ...).
 * Shared by `handleAddSuggestedReference` (existing suggestions tab) and
 * `handleReplaceWithAlternative`/"add alongside" (MINSKY-17131 alternative-citation suggestions)
 * so both flows persist the exact same shape of reference data. Also reused directly by the
 * whole-document "Find (U) Alternatives" view (AboveUCitationsView.jsx), which spans multiple
 * reference marks and therefore can't go through this per-marker hook.
 */
export function buildReferenceDataFromCitation(citation) {
    const localId = generateLocalId({...citation, localId: citation.localId || citation.code || null});
    if (!localId) return null;
    return {
        localId,
        docId: citation.docId,
        segmentId: citation.segmentId,
        index: citation.index,
        offset: citation.offset,
        content: citation.content,
        docTitle: citation.docTitle,
        docSource: citation.docSource,
        docUrl: citation.docUrl,
        sourceScore: citation.sourceScore,
        start: citation.start,
        end: citation.end,
        originalContent: citation.originalContent,
        originalLanguage: citation.originalLanguage,
        tags: citation.tags,
        docClassificationLevel: citation.docClassificationLevel ?? citation.classificationLevel ?? null,
    };
}

/**
 * @param {Object}  params
 * @param {string}  params.markerUid       Reference mark UID
 * @param {string}  params.fullContent     Statement text (for "Find more examples")
 * @param {Array}   params.references      Current added references
 * @param {string}  params.reportId        Report ID (for citation number sorting)
 * @param {Object}  params.view            ProseMirror view instance
 * @param {Function} params.dismissSuggestion  Optimistic dismiss from SuggestedReferencesTab
 */
export function useReferenceMarkerActions({markerUid, fullContent, references, reportId, view, dismissSuggestion}) {
    const ydoc = useYDocStore(state => state.ydoc);
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);
    const getReferenceNumber = useReferenceNumberStore(state => state.getReferenceNumber);
    const openSourcePackageBuilder = useSourcePackageBuilderStore(state => state.open);

    // Sort references by citation number
    const sortedReferences = useMemo(() => {
        if (!Array.isArray(references)) return [];
        return [...references].sort((a, b) => {
            const numA = getReferenceNumber(reportId, a.localId);
            const numB = getReferenceNumber(reportId, b.localId);
            if (numA == null && numB == null) return 0;
            if (numA == null) return 1;
            if (numB == null) return -1;
            return numA - numB;
        });
    }, [references, reportId, getReferenceNumber]);

    // Write forceRefresh flag into Y.Map so the backend picks it up
    const handleRefreshAll = useCallback(() => {
        const {ydoc: doc, yReferenceMarks: yrm} = useYDocStore.getState();
        if (!doc || !yrm || !markerUid) return;
        doc.transact(() => {
            const markMap = yrm.get(markerUid);
            if (!markMap) return;
            let metaMap = markMap.get('_meta');
            if (!metaMap) {
                metaMap = new Y.Map();
                markMap.set('_meta', metaMap);
            }
            metaMap.set('forceRefresh', true);
        }, null);
    }, [markerUid]);

    // Add a suggested reference to the mark
    const handleAddSuggestedReference = useCallback((citation) => {
        if (!citation || !markerUid) {
            console.error('Missing required data to add suggestion:', {citation, markerUid});
            return;
        }
        if (!ydoc || !yReferenceMarks) {
            console.error('Y.Doc not ready');
            return;
        }
        try {
            const referenceData = buildReferenceDataFromCitation(citation);
            if (!referenceData) {
                console.error('Cannot compute localId for citation:', citation);
                return;
            }
            const localId = referenceData.localId;
            // Optimistically remove from suggestions list
            dismissSuggestion?.(localId);
            // Remove from Y.Map so all peers see the change and the counter updates
            removeSuggestedReference(ydoc, yReferenceMarks, markerUid, localId);
            // Write to Y.Map
            addReference(ydoc, yReferenceMarks, markerUid, referenceData);
            if (view && typeof view.focus === 'function') {
                view.focus();
            }
        } catch (error) {
            console.error('❌ Failed to add suggested reference:', error);
        }
    }, [ydoc, yReferenceMarks, markerUid, dismissSuggestion, view]);

    // MINSKY-17131: replace an above-(U) citation with a (U)-classified alternative segment
    // in a single Y.js transaction — remove the old reference, add the new one — so undo (Ctrl+Z)
    // reverts both halves atomically.
    const handleReplaceWithAlternative = useCallback((oldLocalId, alternativeCitation) => {
        if (!oldLocalId || !alternativeCitation || !markerUid) {
            console.error('Missing required data to replace citation:', {oldLocalId, alternativeCitation, markerUid});
            return null;
        }
        if (!ydoc || !yReferenceMarks) {
            console.error('Y.Doc not ready');
            return null;
        }
        const referenceData = buildReferenceDataFromCitation(alternativeCitation);
        if (!referenceData) {
            console.error('Cannot compute localId for alternative citation:', alternativeCitation);
            return null;
        }
        try {
            ydoc.transact(() => {
                removeReferenceMark(ydoc, yReferenceMarks, markerUid, oldLocalId);
                addReference(ydoc, yReferenceMarks, markerUid, referenceData);
            }, null); // null origin → tracked by Y.UndoManager → undoable with Ctrl+Z
            if (view && typeof view.focus === 'function') {
                view.focus();
            }
            return referenceData.localId;
        } catch (error) {
            console.error('❌ Failed to replace citation with alternative:', error);
            return null;
        }
    }, [ydoc, yReferenceMarks, markerUid, view]);

    // MINSKY-17131: add an alternative-citation suggestion alongside the existing citation
    // (rather than replacing it) — a thin, explicitly-named wrapper over the shared add path.
    const handleAddAlternativeAlongside = useCallback((alternativeCitation) => {
        handleAddSuggestedReference(alternativeCitation);
    }, [handleAddSuggestedReference]);

    // Dismiss a suggestion without adding it
    const handleRemoveSuggestedReference = useCallback((citation) => {
        if (!citation?.localId) return;
        dismissSuggestion?.(citation.localId);
        // Remove from Y.Map so all peers see the change and the counter updates
        removeSuggestedReference(ydoc, yReferenceMarks, markerUid, citation.localId);
    }, [dismissSuggestion, ydoc, yReferenceMarks, markerUid]);

    // Open SourcePackageBuilder for "Find more examples"
    const handleFindMoreExamples = useCallback(() => {
        const searchQuery = fullContent || '';
        openSourcePackageBuilder({searchQuery, referenceMarkId: markerUid});
    }, [fullContent, markerUid, openSourcePackageBuilder]);

    // Delete a single reference from Y.Map
    const handleDeleteReference = useCallback((localId) => {
        if (!localId || !ydoc || !yReferenceMarks || !markerUid) {
            console.error('Missing data for reference deletion:', {localId, markerUid});
            return;
        }
        removeReferenceMark(ydoc, yReferenceMarks, markerUid, localId);
        if (view && typeof view.focus === 'function') {
            view.focus();
        }
    }, [ydoc, yReferenceMarks, markerUid, view]);

    return {
        sortedReferences,
        getReferenceNumber,
        handleRefreshAll,
        handleAddSuggestedReference,
        handleRemoveSuggestedReference,
        handleFindMoreExamples,
        handleDeleteReference,
        handleReplaceWithAlternative,
        handleAddAlternativeAlongside,
    };
}

export default useReferenceMarkerActions;

